import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Sparkles } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "./ui/sheet";
import { useApply } from "./ApplyContext";
import LanguageSwitcher from "./LanguageSwitcher";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/courses", label: "Courses" },
  { to: "/faculty", label: "Faculty" },
  { to: "/gallery", label: "Gallery" },
  { to: "/vacancies", label: "Vacancies" },
  { to: "/admission", label: "Admission" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const location = useLocation();
  const { open: openApply } = useApply();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setSheetOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl shadow-[0_4px_24px_rgba(15,76,58,0.08)] border-b border-brand-primary/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link
            to="/"
            className="flex items-center"
            data-testid="brand-link"
            aria-label="Jawad Ali Shah Imambara — Home"
          >
            <img
              src="/uploads/logo.png"
              alt="Jawad Ali Shah Imambara Girls Post Graduate College"
              className="h-10 lg:h-14 w-auto"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `nav-link text-sm font-medium text-brand-ink hover:text-brand-primary ${
                    isActive ? "active text-brand-primary" : ""
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <LanguageSwitcher />
          </nav>

          {/* Mobile - sheet */}
          <div className="lg:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="menu-trigger"
                  className="p-2 rounded-full bg-brand-accent text-brand-primary"
                  aria-label="Open menu"
                >
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-white p-0">
                <SheetHeader className="p-6 bg-brand-primary text-white">
                  <SheetTitle className="text-white text-left font-serif text-xl flex items-center gap-3">
                    <img src="/uploads/logo.png" alt="Logo" className="h-10 w-auto bg-white rounded-md p-1" />
                    Menu
                  </SheetTitle>
                </SheetHeader>
                <div className="flex flex-col">
                  {NAV_LINKS.map((l) => (
                    <SheetClose asChild key={l.to}>
                      <Link
                        to={l.to}
                        data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                        className="px-6 py-4 border-b border-brand-primary/10 text-brand-ink hover:bg-brand-accent hover:text-brand-primary transition-colors flex items-center justify-between"
                      >
                        <span className="font-medium">{l.label}</span>
                        <span className="text-brand-secondary">→</span>
                      </Link>
                    </SheetClose>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setSheetOpen(false);
                      setTimeout(() => openApply(), 250);
                    }}
                    data-testid="mobile-nav-apply"
                    className="px-6 py-4 bg-brand-secondary text-brand-primary font-semibold flex items-center justify-between hover:bg-brand-primary hover:text-brand-secondary transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      Apply Now
                    </span>
                    <span>→</span>
                  </button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
